import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./ScrollReveal.module.scss";

interface ScrollRevealProps {
  children: React.ReactNode;
  animation?: "fade-up" | "fade-in" | "scale-up" | "slide-right" | "slide-left";
  delay?: number; // in ms
  threshold?: number; // 0 to 1
  className?: string;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  animation = "fade-up",
  delay = 0,
  threshold = 0.1,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  const style = {
    transitionDelay: `${delay}ms`,
  };

  const visibleClass = styles["is-visible"] || "is-visible";

  return (
    <div
      ref={ref}
      className={classNames(
        styles["scroll-reveal"],
        styles[animation],
        { [visibleClass]: isVisible },
        className,
      )}
      style={style}>
      {children}
    </div>
  );
};
